import { useCallback } from 'react';

export interface UseCanvasTouchProps {
  // Current state
  stagePos: { x: number; y: number };
  stageScale: number;

  // Refs
  canvasContainerRef: React.RefObject<HTMLDivElement | null>;
  isTouchPanningRef: React.MutableRefObject<boolean>;
  lastTouchDistanceRef: React.MutableRefObject<number | null>;
  lastTouchCenterRef: React.MutableRefObject<{ x: number; y: number } | null>;
  lastSingleTouchPosRef: React.MutableRefObject<{ x: number; y: number } | null>;

  // Setters
  setStagePos: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  setStageScale: React.Dispatch<React.SetStateAction<number>>;

  // Mouse event handlers (to simulate)
  handleStageMouseDown: (e: any) => void;
  handleStageMouseMove: (e: any) => void;
  handleStageMouseUp: (e: any) => void;
}

export interface UseCanvasTouchReturn {
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: (e: React.TouchEvent) => void;
}

export function useCanvasTouch({
  stagePos,
  stageScale,
  canvasContainerRef,
  isTouchPanningRef,
  lastTouchDistanceRef,
  lastTouchCenterRef,
  lastSingleTouchPosRef,
  setStagePos,
  setStageScale,
  handleStageMouseDown,
  handleStageMouseMove,
  handleStageMouseUp,
}: UseCanvasTouchProps): UseCanvasTouchReturn {

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Only handle touches on canvas or its children
    const isCanvasInteraction = e.target === canvasContainerRef.current || canvasContainerRef.current?.contains(e.target as Node);
    if (!isCanvasInteraction) return;

    if (e.touches.length === 1) {
      // Ignore if pinch zooming
      if (isTouchPanningRef.current) return;

      const touch = e.touches[0];
      const rect = canvasContainerRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Simulate mouse event
      const fakeEvent = {
        evt: { ...e.nativeEvent, button: 0, buttons: 1, preventDefault: () => { } },
        cancelBubble: false,
        target: {
          getStage: () => ({
            getPointerPosition: () => ({
              x: touch.clientX - rect.left,
              y: touch.clientY - rect.top
            }),
            scaleX: () => stageScale,
            scaleY: () => stageScale,
            x: () => stagePos.x,
            y: () => stagePos.y,
            container: () => canvasContainerRef.current
          }),
          getParent: () => null
        }
      };

      handleStageMouseDown(fakeEvent);

    } else if (e.touches.length === 2) {
      // Start pinch zoom/pan
      const p1 = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      const p2 = { x: e.touches[1].clientX, y: e.touches[1].clientY };

      const distance = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
      const center = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };

      lastTouchDistanceRef.current = distance;
      lastTouchCenterRef.current = center;
      isTouchPanningRef.current = true;
    }
  }, [handleStageMouseDown, stageScale, stagePos, canvasContainerRef, isTouchPanningRef, lastTouchDistanceRef, lastTouchCenterRef]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    // Prevent browser scroll (very important)
    if (e.cancelable) e.preventDefault();

    if (e.touches.length === 1) {
      if (isTouchPanningRef.current) return;

      const touch = e.touches[0];
      const rect = canvasContainerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const curPos = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
      lastSingleTouchPosRef.current = curPos;

      const fakeEvent = {
        evt: e.nativeEvent,
        cancelBubble: false,
        target: {
          getStage: () => ({
            getPointerPosition: () => curPos,
            scaleX: () => stageScale,
            scaleY: () => stageScale,
            x: () => stagePos.x,
            y: () => stagePos.y,
            container: () => canvasContainerRef.current
          }),
          getParent: () => null
        }
      };
      handleStageMouseMove(fakeEvent);

    } else if (e.touches.length === 2 && isTouchPanningRef.current) {
      const p1 = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      const p2 = { x: e.touches[1].clientX, y: e.touches[1].clientY };

      const distance = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
      const center = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };

      if (lastTouchDistanceRef.current && lastTouchCenterRef.current) {
        // Zoom calculation with sensitivity adjustment
        const rawScaleBy = distance / lastTouchDistanceRef.current;
        const sensitivity = 0.5; // Sensitivity (higher = faster, 1.0 is default)
        const scaleBy = 1 + (rawScaleBy - 1) * sensitivity;

        const newScale = stageScale * scaleBy;
        const clampedScale = Math.min(Math.max(newScale, 0.1), 5);

        // Zoom coordinate correction based on pinch center point
        const oldScale = stageScale;
        const centerX = center.x - (canvasContainerRef.current?.getBoundingClientRect().left || 0);
        const centerY = center.y - (canvasContainerRef.current?.getBoundingClientRect().top || 0);

        const localPointX = (centerX - stagePos.x) / oldScale;
        const localPointY = (centerY - stagePos.y) / oldScale;

        const newStagePosX = centerX - localPointX * clampedScale;
        const newStagePosY = centerY - localPointY * clampedScale;

        // Add pan (movement) delta
        const panDx = center.x - lastTouchCenterRef.current.x;
        const panDy = center.y - lastTouchCenterRef.current.y;

        setStageScale(clampedScale);
        setStagePos({ x: newStagePosX + panDx, y: newStagePosY + panDy });
      }

      lastTouchDistanceRef.current = distance;
      lastTouchCenterRef.current = center;
    }
  }, [handleStageMouseMove, stageScale, stagePos, canvasContainerRef, isTouchPanningRef, lastTouchDistanceRef, lastTouchCenterRef, lastSingleTouchPosRef, setStageScale, setStagePos]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (isTouchPanningRef.current && e.touches.length < 2) {
      isTouchPanningRef.current = false;
      lastTouchDistanceRef.current = null;
      lastTouchCenterRef.current = null;
    }

    // Simulate MouseUp when touch ends (on last touch release)
    if (e.touches.length === 0) {
      handleStageMouseUp({
        evt: { button: 0 },
        target: {
          getStage: () => ({
            container: () => canvasContainerRef.current,
            getPointerPosition: () => lastSingleTouchPosRef.current || { x: 0, y: 0 },
            scaleX: () => stageScale,
            scaleY: () => stageScale,
            x: () => stagePos.x,
            y: () => stagePos.y,
          })
        }
      });
      lastSingleTouchPosRef.current = null;
    }
  }, [handleStageMouseUp, stageScale, stagePos, canvasContainerRef, isTouchPanningRef, lastTouchDistanceRef, lastTouchCenterRef, lastSingleTouchPosRef]);

  return { handleTouchStart, handleTouchMove, handleTouchEnd };
}
